/**
 * Stating a markup profile as markup or as gross margin.
 *
 * Operators think in whichever figure their trade quotes in, but a profile can
 * only be stored one way or saved schedules would change meaning the day the
 * other basis appeared. `MarkupRange.percent` stays canonical markup — every
 * pricing path (`resolveMarkupPercent`, the preview, materialized documents)
 * reads it as markup and none of them know this screen exists. The basis the
 * operator typed in rides along on the range purely so the editor reopens
 * showing the figure they entered.
 *
 * Ranges saved before the toggle existed carry no basis and read as markup,
 * which is exactly what they always were.
 */

import { marginFromMarkup, markupFromMargin, type UpliftBasis } from '@/lib/sales/markup-margin';
import type { MarkupRange } from '@/types/pricebook';

/**
 * A range mid-edit. `value` is in the draft's basis — margin when that is what
 * the operator picked — which is why it is not called `percent`: only the
 * stored shape is canonical markup, and mixing the two up is the whole hazard
 * this module exists to contain.
 */
export interface MarkupRangeDraft {
  minCost: number;
  maxCost: number | null;
  value: number;
}

/** The basis a saved profile was typed in; markup for anything older. */
export function profileBasis(ranges: MarkupRange[] | undefined | null): UpliftBasis {
  return ranges?.find((range) => range.basis)?.basis ?? 'MARKUP';
}

/** Restates a stored markup as the figure shown for `basis`. */
export function displayValue(markupPercent: number, basis: UpliftBasis): number {
  return basis === 'MARGIN' ? marginFromMarkup(markupPercent) : markupPercent;
}

/** Opens a saved profile for editing in the basis it was typed in. */
export function draftRangesFromProfile(
  ranges: MarkupRange[],
  basis: UpliftBasis,
): MarkupRangeDraft[] {
  return ranges.map((range) => ({
    minCost: range.minCost,
    maxCost: range.maxCost,
    value: displayValue(range.percent, basis),
  }));
}

/**
 * Restates the typed values when the toggle flips, so the money is unchanged —
 * 50% markup becomes 33.33% margin, the same $150 on $100 of cost. Both figures
 * are held to cents, so flipping back and forth can settle a hundredth away
 * from where it started; that is rounding, not a different price.
 */
export function restateDraftRanges(
  rows: MarkupRangeDraft[],
  from: UpliftBasis,
  to: UpliftBasis,
): MarkupRangeDraft[] {
  if (from === to) return rows;
  return rows.map((row) => ({
    ...row,
    value: displayValue(storedPercent(row.value, from), to),
  }));
}

/** The canonical markup behind a value typed in `basis`. */
export function storedPercent(value: number, basis: UpliftBasis): number {
  return basis === 'MARGIN' ? markupFromMargin(value) : value;
}

/** The payload shape: canonical markup, with the typed basis recorded. */
export function storedRangesFromDraft(rows: MarkupRangeDraft[], basis: UpliftBasis): MarkupRange[] {
  return rows.map((row) => ({
    minCost: row.minCost,
    maxCost: row.maxCost,
    percent: storedPercent(row.value, basis),
    basis,
  }));
}

/** Column header and input label, so the figure being typed is never a guess. */
export function basisFieldLabel(basis: UpliftBasis): string {
  return basis === 'MARGIN' ? 'Gross margin %' : 'Markup %';
}

/**
 * The other figure, for the read-only hint beside the input. Nobody should have
 * to work out that 50% markup is 33.33% margin while entering a schedule.
 */
export function counterpartHint(value: number, basis: UpliftBasis): string {
  if (basis === 'MARGIN' && value >= 100) {
    // Saying "= 0% markup" here was a lie of omission: 0 is what the conversion
    // RETURNS for an impossible margin, not what the operator asked for.
    return 'not reachable — see below';
  }
  return basis === 'MARGIN'
    ? `= ${markupFromMargin(value)}% markup`
    : `= ${marginFromMarkup(value)}% margin`;
}

/**
 * Gross margin at which the markup it implies stops being a sane thing to type
 * by accident.
 *
 * Margin is measured against PRICE, so it approaches 100% asymptotically: 90%
 * margin is already a 900% markup, and 99% is 9900% — which turned a $2,310
 * base into a $231,024 price for an operator who read "99%" as "99% profit".
 * The arithmetic was right and the screen said nothing.
 */
const EXTREME_MARGIN_PERCENT = 90;

export type UpliftBasisIssue =
  /** Above 100% is not a margin at all — cost would have to be negative. */
  | { kind: 'unreachable'; markupEquivalent: null }
  /** Legal, but multiplies the price far more than the number suggests. */
  | { kind: 'extreme'; markupEquivalent: number };

/**
 * What is wrong, if anything, with a percentage typed on this basis.
 *
 * MARKUP has no ceiling — 300% markup is an ordinary thing to want — so it
 * never returns an issue. Only margin does, because only margin has a
 * denominator that runs out.
 */
export function upliftBasisIssue(value: number, basis: UpliftBasis): UpliftBasisIssue | null {
  if (basis !== 'MARGIN' || !Number.isFinite(value)) return null;
  if (value >= 100) return { kind: 'unreachable', markupEquivalent: null };
  if (value >= EXTREME_MARGIN_PERCENT) {
    return { kind: 'extreme', markupEquivalent: markupFromMargin(value) };
  }
  return null;
}

/** The multiplier a gross margin applies to cost: `1 / (1 - margin)`. */
export function marginPriceMultiplier(value: number): number {
  if (!Number.isFinite(value) || value >= 100) return 0;
  return Math.round((100 / (100 - value)) * 100) / 100;
}

/** A stored range's uplift, spelled out in the basis it was typed in. */
export function formatUplift(markupPercent: number, basis: UpliftBasis): string {
  return `${displayValue(markupPercent, basis)}% ${basis === 'MARGIN' ? 'margin' : 'markup'}`;
}
